import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { scheduleApi, storesApi, visitsApi } from '../services/api';
import { Calendar } from '../components/Calendar';
import { ScheduleList } from '../components/ScheduleList';
import { DayDetailDrawer } from '../components/DayDetailDrawer';
import { VisitPopup } from '../components/VisitPopup';
import { formatDate } from 'shared';
import type { VisitWithStore } from 'shared';

export function Schedule() {
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<'week' | 'month' | 'list'>('week');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedVisit, setSelectedVisit] = useState<VisitWithStore | null>(null);

  const moveVisitMutation = useMutation({
    mutationFn: ({ visitId, toDate }: { visitId: string; toDate: string }) => {
      const visit = visits.find((v) => v.id === visitId);
      if (!visit) throw new Error('Visit not found');
      const timeStr = visit.start_datetime.split('T')[1]; // keep same time
      return visitsApi.update(visitId, {
        start_datetime: `${toDate}T${timeStr}`,
        duration_minutes: visit.duration_minutes,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['schedule'] }),
  });

  // Calculate date range based on view mode
  const now = new Date();
  const [startDate, setStartDate] = useState(() => {
    const start = new Date(now);
    start.setDate(start.getDate() - start.getDay()); // Start of week
    return formatDate(start);
  });

  const endDate = (() => {
    const start = new Date(startDate);
    if (viewMode === 'week') {
      start.setDate(start.getDate() + 6);
    } else {
      start.setMonth(start.getMonth() + 1);
      start.setDate(0); // Last day of month
    }
    return formatDate(start);
  })();

  const { data: visits = [], isLoading } = useQuery({
    queryKey: ['schedule', startDate, endDate],
    queryFn: () => scheduleApi.list(startDate, endDate),
  });

  const { data: stores = [] } = useQuery({
    queryKey: ['stores'],
    queryFn: () => storesApi.list(),
  });

  const hasNoRevenue = stores.length > 0 && stores.every((s) => !s.priority?.ytd_revenue_cents);

  const generateMutation = useMutation({
    mutationFn: () => {
      // Generate for next 4 weeks
      const generateEnd = new Date();
      generateEnd.setDate(generateEnd.getDate() + 28);
      return scheduleApi.generate({
        start_date: formatDate(new Date()),
        end_date: formatDate(generateEnd),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedule'] });
    },
  });

  const handlePrevious = () => {
    const start = new Date(startDate);
    if (viewMode === 'week') {
      start.setDate(start.getDate() - 7);
    } else {
      start.setMonth(start.getMonth() - 1);
    }
    setStartDate(formatDate(start));
  };

  const handleNext = () => {
    const start = new Date(startDate);
    if (viewMode === 'week') {
      start.setDate(start.getDate() + 7);
    } else {
      start.setMonth(start.getMonth() + 1);
    }
    setStartDate(formatDate(start));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Schedule</h2>
        <div className="flex items-center gap-4">
          {/* View Toggle */}
          <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
            {(['week', 'month', 'list'] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                style={{
                  padding: '8px 12px',
                  fontSize: '14px',
                  fontWeight: 500,
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: viewMode === mode ? '#2563eb' : '#fff',
                  color: viewMode === mode ? '#fff' : '#374151',
                }}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>

          {/* Generate Button */}
          <button
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            {generateMutation.isPending ? 'Generating...' : 'Generate Schedule'}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrevious}
          className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
        >
          &larr; Previous
        </button>
        <span className="text-lg font-medium text-gray-900">
          {startDate} to {endDate}
        </span>
        <button
          onClick={handleNext}
          className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
        >
          Next &rarr;
        </button>
      </div>

      {/* No Revenue Warning */}
      {hasNoRevenue && (
        <div style={{ padding: '12px 16px', backgroundColor: '#fef3c7', borderRadius: '6px', color: '#92400e', fontSize: '14px' }}>
          No revenue data entered. Schedule generation uses default visit durations. <a href="/revenue" style={{ textDecoration: 'underline' }}>Add revenue →</a>
        </div>
      )}

      {/* Calendar/List */}
      {isLoading ? (
        <div className="text-center py-8">Loading schedule...</div>
      ) : viewMode === 'list' ? (
        <ScheduleList visits={visits} />
      ) : (
        <Calendar
          visits={visits}
          startDate={startDate}
          endDate={endDate}
          viewMode={viewMode === 'month' ? 'month' : 'week'}
          onDayClick={(date) => setSelectedDate(date)}
          onVisitClick={(visit) => setSelectedVisit(visit)}
          onVisitDrop={(visitId, _fromDate, toDate) => moveVisitMutation.mutate({ visitId, toDate })}
        />
      )}

      {generateMutation.isSuccess && (
        <p className="text-green-600 text-sm">
          Generated {generateMutation.data?.visits_created} visits!
        </p>
      )}
      {generateMutation.isError && (
        <p className="text-red-600 text-sm">
          {generateMutation.error instanceof Error ? generateMutation.error.message : 'Failed to generate schedule. Please try again.'}
        </p>
      )}

      {/* Day Detail Drawer */}
      {selectedDate && !selectedVisit && (
        <DayDetailDrawer
          date={selectedDate}
          visits={visits.filter((v) => v.start_datetime.split('T')[0] === selectedDate)}
          onClose={() => setSelectedDate(null)}
        />
      )}

      {/* Visit Popup */}
      {selectedVisit && (
        <VisitPopup
          visit={selectedVisit}
          onClose={() => setSelectedVisit(null)}
        />
      )}
    </div>
  );
}
